import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import doctorService from '../../../services/doctorService';
import axios from 'axios';
import '../../../styles/components/doctor/JournalEntryDetail.css';

import aiIconUrl from '../../../assets/icons/AI_icon.svg';
import journalIconUrl from '../../../assets/logo/journal_indicator.png';

const ClinicalJournalReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState({
    sentiment: '',
    emotions: [],
    notes: '',
    flags: []
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [useAI, setUseAI] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  
  // Safe date formatting function
  const formatDateSafe = (dateString) => {
    try {
      if (!dateString) return 'Unknown Date';
      
      let parsedDate;
      
      try {
        parsedDate = parseISO(dateString);
      } catch {
        parsedDate = new Date(dateString);
      }
      
      if (!isValid(parsedDate)) {
        return 'Invalid Date';
      }
      
      return format(parsedDate, 'MMMM dd, yyyy | h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown Date';
    }
  };
  
  // Common emotions for quick selection
  const commonEmotions = [
    'Joy', 'Sadness', 'Fear', 'Anger', 'Surprise', 'Disgust', 
    'Trust', 'Anticipation', 'Gratitude', 'Confusion', 'Loneliness', 
    'Anxiety', 'Hope', 'Longing', 'Grief', 'Relief'
  ];
  
  // Fetch entry details
  useEffect(() => {
    const fetchEntry = async () => {
      try {
        setLoading(true);
        const data = await doctorService.getJournalEntry(id);
        
        console.log('Journal entry data:', data);
        
        setEntry(data);
        
        // Initialize analysis form with existing analysis if available
        if (data.sentimentAnalysis?.sentiment || data.emotions || data.doctorNotes || data.flags) {
          setAnalysis({
            sentiment: data.sentimentAnalysis?.sentiment?.type || '',
            emotions: Array.isArray(data.sentimentAnalysis?.emotions) 
              ? data.sentimentAnalysis.emotions.map(e => typeof e === 'object' ? e.name : e)
              : data.emotions || [],
            notes: data.doctorNotes || '',
            flags: data.flags || []
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching entry:', error);
        setError(error.response?.data?.message || 'Failed to load journal entry. Please try again.');
        setLoading(false);
      }
    };
    
    fetchEntry();
  }, [id]);
  
  // Handle analysis form changes
  const handleAnalysisChange = (field, value) => {
    setAnalysis({
      ...analysis,
      [field]: value
    });
  };
  
  // Save analysis
  const saveAnalysis = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await doctorService.analyzeJournalEntry(id, {
        ...analysis,
        useAI: useAI && !aiAnalysis
      });
      
      setSaveSuccess(true);
      
      // Update local entry data
      const updatedEntry = { ...entry };
      
      if (!updatedEntry.sentimentAnalysis) {
        updatedEntry.sentimentAnalysis = {};
      }
      
      updatedEntry.sentimentAnalysis.sentiment = { type: analysis.sentiment };
      updatedEntry.sentimentAnalysis.emotions = analysis.emotions.map(e => ({ name: e }));
      updatedEntry.doctorNotes = analysis.notes;
      updatedEntry.flags = analysis.flags;
      updatedEntry.isAnalyzed = true;
      
      setEntry(updatedEntry);
      
      if (response.aiAnalysis) {
        setAiAnalysis(response.aiAnalysis);
      }
      
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving analysis:', error);
      setError(error.response?.data?.message || 'Failed to save analysis. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Run AI analysis manually
  const runAIAnalysis = async () => {
    setAiLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      console.log(`Starting AI analysis for entry ID: ${id}`);
      
      const response = await axios.post(
        `/api/doctor/journal-entries/${id}/analyze`,
        { 
          useAI: true, 
          applyChanges: true,
          includeHighlights: true
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          timeout: 20000
        }
      );
      
      console.log('Raw AI analysis response:', response.data);
      
      let analysisData = null;
      
      if (response.data && response.data.success === true) {
        if (response.data.data && response.data.data.analysis) {
          analysisData = response.data.data.analysis;
          console.log('Found analysis in data.analysis:', analysisData);
        } else if (response.data.data && response.data.data.sentimentAnalysis) {
          analysisData = response.data.data.sentimentAnalysis;
          console.log('Found analysis in data.sentimentAnalysis:', analysisData);
        } else if (response.data.aiAnalysis) {
          analysisData = response.data.aiAnalysis;
          console.log('Found analysis in aiAnalysis:', analysisData);
        } else if (response.data.data) {
          analysisData = response.data.data;
          console.log('Using data directly:', analysisData);
        } else {
          analysisData = response.data;
          console.log('Using response.data:', analysisData);
        }
      }
      
      console.log('Final analysisData:', analysisData);
      
      if (analysisData) {
        setAiAnalysis({
          sentiment: analysisData.sentiment || { type: 'neutral', score: 0.5 },
          emotions: analysisData.emotions || [],
          insights: analysisData.insights || [],
          recommendedActions: analysisData.recommendedActions || [],
          highlights: analysisData.highlights || [],
          keywords: analysisData.keywords || []
        });
        
        setAnalysis(prev => {
          const updatedAnalysis = { ...prev };
          
          if (analysisData.sentiment && analysisData.sentiment.type) {
            updatedAnalysis.sentiment = analysisData.sentiment.type;
          }
          
          if (analysisData.emotions && Array.isArray(analysisData.emotions)) {
            updatedAnalysis.emotions = analysisData.emotions.map(e => 
              typeof e === 'object' ? (e.name || e.label || e) : e
            );
          }
          
          if (analysisData.insights && Array.isArray(analysisData.insights)) {
            updatedAnalysis.notes = analysisData.insights.join('\n');
          }
          
          if (analysisData.recommendedActions && Array.isArray(analysisData.recommendedActions)) {
            updatedAnalysis.flags = ['progress'];
          }
          
          console.log('Updated analysis state:', updatedAnalysis);
          return updatedAnalysis;
        });
        
        setError(null);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        
      } else {
        throw new Error('Could not extract analysis data from response');
      }
    } catch (error) {
      console.error('Error running AI analysis:', error);
      
      let errorMessage = 'Failed to run AI analysis. ';
      
      if (error.response) {
        errorMessage += `Server responded with status ${error.response.status}: ${error.response.data?.message || 'Unknown error'}`;
        console.error('Error response data:', error.response.data);
      } else if (error.request) {
        errorMessage += 'No response received from server. Please check your network connection.';
      } else {
        errorMessage += error.message || 'Unknown error occurred';
      }
      
      setError(errorMessage);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock AI analysis data for development');
        const mockAnalysis = {
          sentiment: { type: 'neutral', score: 50 },
          emotions: [
            { name: 'calm', score: 0.7 },
            { name: 'thoughtful', score: 0.5 },
            { name: 'neutral', score: 0.3 }
          ],
          highlights: [
            {
              text: "Just try to survive another day. I don't see any hope for things getting better.",
              keyword: "hope",
              type: "negative"
            },
            {
              text: "You're failing at everything important. Everyone would be better off without your constant mistakes.",
              keyword: "failing",
              type: "negative"
            }
          ]
        };
        
        setAiAnalysis(mockAnalysis);
        
        setAnalysis(prev => ({
          ...prev,
          sentiment: mockAnalysis.sentiment.type,
          emotions: mockAnalysis.emotions.map(e => e.name)
        }));
      }
    } finally {
      setAiLoading(false);
    }
  };
  
  // Toggle emotions in the analysis
  const toggleEmotion = (emotion) => {
    const emotions = [...analysis.emotions];
    const index = emotions.indexOf(emotion);
    
    if (index >= 0) {
      emotions.splice(index, 1);
    } else {
      emotions.push(emotion);
    }
    
    handleAnalysisChange('emotions', emotions);
  };
  
  // Toggle flags in the analysis
  const toggleFlag = (flag) => {
    const flags = [...analysis.flags];
    const index = flags.indexOf(flag);
    
    if (index >= 0) {
      flags.splice(index, 1);
    } else {
      flags.push(flag);
    }
    
    handleAnalysisChange('flags', flags);
  };
  
  // Render journal content
  const renderJournalContent = () => {
    if (entry?.rawText) {
      return (
        <div className="clinical-journal-text">
          {entry.rawText}
        </div>
      );
    }
    
    if (entry?.template?.fields && Array.isArray(entry.template.fields) && entry.template.fields.length > 0) {
      return entry.template.fields.map((field, index) => (
        <div key={field.id || `field-${index}`} className="clinical-field-section">
          <h4 className="clinical-field-label">{field.label}</h4>
          {renderFieldResponse(field)}
        </div>
      ));
    }
    
    if (entry?.responses && Object.keys(entry.responses).length > 0) {
      return Object.entries(entry.responses).map(([key, value], index) => (
        <div key={key} className="clinical-field-section">
          <h4 className="clinical-field-label">
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </h4>
          <p>{typeof value === 'object' ? JSON.stringify(value) : value}</p>
        </div>
      ));
    }
    
    if (entry?.journalFields && Object.keys(entry.journalFields).length > 0) {
      return Object.entries(entry.journalFields).map(([key, value], index) => (
        <div key={key} className="clinical-field-section">
          <h4 className="clinical-field-label">
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </h4>
          <p>
            {Array.isArray(value) 
              ? value.join(', ') 
              : (typeof value === 'object' ? JSON.stringify(value) : value)}
          </p>
        </div>
      ));
    }
    
    return (
      <div className="clinical-no-content">
        <p>No journal content found</p>
      </div>
    );
  };
  
  // Render a form field's response
  const renderFieldResponse = (field) => {
    if (!entry || !field || !entry.responses) return <p>No response</p>;
    
    const response = entry.responses[field.id];
    if (response === undefined || response === null) return <p>No response</p>;
    
    switch (field.type) {
      case 'mood-scale':
        if (!field.options) return <p>{response}</p>;
        const option = field.options.find(opt => opt.value === response || opt.value === response.toString());
        return (
          <div className="clinical-mood-response">
            <span className="clinical-mood-icon">{option?.icon || ''}</span>
            <span className="clinical-mood-label">{option?.label || response || 'No response'}</span>
          </div>
        );
        
      case 'select':
      case 'radio':
        if (!field.options) return <p>{response}</p>;
        const selectedOption = field.options.find(opt => opt.value === response || opt.value === response.toString());
        return <p>{selectedOption?.label || response || 'No response'}</p>;
        
      case 'checkbox':
        if (Array.isArray(response)) {
          if (response.length === 0) return <p>No options selected</p>;
          
          return (
            <div className="clinical-checkbox-responses">
              {response.map((value, index) => {
                const opt = field.options ? field.options.find(o => o.value === value || o.value === value.toString()) : null;
                return (
                  <span key={index} className="clinical-response-chip">
                    {opt?.label || value}
                  </span>
                );
              })}
            </div>
          );
        } else {
          return <p>Invalid response format</p>;
        }
        
      case 'textarea':
        return <p className="clinical-textarea-response">{response || 'No response'}</p>;
        
      case 'date':
        try {
          return <p>{format(new Date(response), 'MMMM dd, yyyy')}</p>;
        } catch {
          return <p>{response}</p>;
        }
        
      default:
        return <p>{typeof response === 'object' ? JSON.stringify(response) : response || 'No response'}</p>;
    }
  };
  
  if (loading) {
    return (
      <div className="clinical-loading-container">
        <div className="clinical-loading-spinner"></div>
      </div>
    );
  }
  
  if (error && !entry) {
    return (
      <div className="clinical-error-alert">
        <span className="clinical-alert-title">Error:</span>
        {error}
      </div>
    );
  }
  
  return (
    <div className="clinical-journal-review">
      
      {/* Alerts */}
      {error && (
        <div className="clinical-error-alert">
          <span className="clinical-alert-title">Error:</span>
          {error}
        </div>
      )}
      
      {saveSuccess && (
        <div className="clinical-success-alert">
          <span className="clinical-alert-title">Success:</span>
          Analysis saved successfully!
        </div>
      )}
      
      {/* Entry Card */}
      <div className="clinical-entry-card">
        {/* Back Button */}
        <button 
          className="clinical-back-btn" 
          onClick={() => navigate('/doctor/journal-entries')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        
        {/* Header Section */}
        <div className="clinical-entry-header">
          <div className="clinical-entry-icon">
            <img src={journalIconUrl} alt="Journal Entry" />
          </div>
          <div className="clinical-entry-meta">
            <h1 className="clinical-entry-date">
              {entry && formatDateSafe(entry.date || entry.createdAt)}
            </h1>
            <p className="clinical-entry-author">
              by {entry?.patientName || (entry?.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'Unknown')}
              {entry?.user?.email && ` (${entry.user.email})`}
            </p>
          </div>
        </div>
        
        {/* Content Section */}
        <div className="clinical-entry-content">
          {renderJournalContent()}
        </div>
      </div>
    </div>
  );
};

export default ClinicalJournalReview;